// Shared UI primitives for the AutoCycle app. Pure presentational components built on
// the suite design tokens (styles.js). Amber (#F59E0B) is AutoCycle's accent.
import { useState, useEffect } from "react";
import { C, font, mono, card, badge } from "../../../lib/styles.js";

export { C, font, mono, card, badge };

// The RealityScan → Blender pipeline, in order. Used by the Processing progress strip.
export const PIPELINE_STEPS = [
  "Align",
  "GCP / Region",
  "Mesh",
  "Simplify",
  "Texture",
  "Export",
  "Convert",
];

// Status → accent color. Queued amber, processing purple, complete green, failed red.
export const STATUS_COLORS = {
  queued: C.amber,
  processing: C.purple,
  complete: C.green,
  failed: C.danger,
};
export const STATUS_LABELS = {
  queued: "Queued",
  processing: "Processing",
  complete: "Complete",
  failed: "Failed",
};

export const eyebrow = {
  fontSize: 10,
  fontWeight: 400,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: C.muted,
  marginBottom: 6,
  fontFamily: font,
};

// Collapses layouts on narrow screens (mirrors Dashboard.jsx's useIsMobile).
export function useIsMobile(breakpoint = 760) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

export function PageWrap({ children }) {
  return <div style={{ maxWidth: 880, margin: "0 auto", fontFamily: font }}>{children}</div>;
}

export function SectionHeader({ eyebrow: eb, title, subtitle, right }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        marginBottom: 18,
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div>
        {eb && <div style={eyebrow}>{eb}</div>}
        <div style={{ fontSize: 20, fontWeight: 500, letterSpacing: "-0.01em", color: C.text }}>
          {title}
        </div>
        {subtitle && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {right && <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>{right}</div>}
    </div>
  );
}

export function StatPill({ value, label, color = C.text }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1 }}>{value}</div>
      <div
        style={{
          fontSize: 9,
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          color: C.muted,
          marginTop: 4,
        }}
      >
        {label}
      </div>
    </div>
  );
}

export function StatusPill({ status }) {
  const color = STATUS_COLORS[status] || C.muted;
  return <span style={badge(color)}>{STATUS_LABELS[status] || status}</span>;
}

export function Chip({ children, color = C.muted }) {
  return (
    <span
      style={{
        fontSize: 10,
        padding: "2px 8px",
        borderRadius: 3,
        background: `${color}1a`,
        color,
        border: `1px solid ${color}38`,
        fontFamily: font,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

// Group jobs by their parent shoot, preserving first-seen order.
export function groupByShoot(jobs) {
  const order = [];
  const map = new Map();
  for (const j of jobs) {
    const shoot = j.shoot || { id: "unassigned", label: "Unassigned" };
    if (!map.has(shoot.id)) {
      map.set(shoot.id, { shoot, jobs: [] });
      order.push(shoot.id);
    }
    map.get(shoot.id).jobs.push(j);
  }
  return order.map((id) => map.get(id));
}

// Collapsible shoot heading with a scan count. Open by default.
export function ShootGroup({ shoot, count, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ marginBottom: 20 }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          cursor: "pointer",
          padding: "0 2px 8px",
          marginBottom: 10,
          borderBottom: `1px solid ${C.border}`,
          userSelect: "none",
        }}
      >
        <span style={{ fontSize: 10, color: C.muted, width: 9 }}>{open ? "▾" : "▸"}</span>
        <span style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{shoot.label}</span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: C.muted }}>
          {count} scan{count === 1 ? "" : "s"}
        </span>
      </div>
      {open && children}
    </div>
  );
}

export function EmptyState({ title, body, action }) {
  return (
    <div
      style={{
        ...card,
        padding: "40px 24px",
        textAlign: "center",
        color: C.muted,
        fontSize: 13,
        fontWeight: 300,
      }}
    >
      <div style={{ fontSize: 14, color: C.text, marginBottom: 6 }}>{title}</div>
      {body && <p style={{ margin: "0 auto 14px", maxWidth: 420, lineHeight: 1.5 }}>{body}</p>}
      {action}
    </div>
  );
}

// Job card with a status-colored left accent (the Dashboard "Today's Briefing" pattern).
export function JobCard({ status, children, onClick, selected }) {
  const color = STATUS_COLORS[status] || C.muted;
  return (
    <div
      onClick={onClick}
      style={{
        ...card,
        padding: 0,
        marginBottom: 8,
        borderLeft: `3px solid ${color}`,
        cursor: onClick ? "pointer" : "default",
        background: selected ? `${color}08` : C.surface,
        transition: "background 0.15s ease",
      }}
    >
      <div style={{ padding: "14px 18px" }}>{children}</div>
    </div>
  );
}

// Right-side slide-in drawer (used for New Job + Recipes). Full-width on mobile.
export function Drawer({ title, subtitle, onClose, children, footer }) {
  const isMobile = useIsMobile();
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(26,26,26,0.35)",
        zIndex: 1000,
        display: "flex",
        justifyContent: "flex-end",
        animation: "fadeUp 0.15s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isMobile ? "100%" : 480,
          maxWidth: "100%",
          height: "100%",
          background: C.bg,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.12)",
          fontFamily: font,
        }}
      >
        <div
          style={{
            padding: "18px 22px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            background: C.surface,
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 500, color: C.text }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              color: C.muted,
              fontSize: 20,
              cursor: "pointer",
              lineHeight: 1,
              padding: 2,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 22 }}>{children}</div>
        {footer && (
          <div style={{ padding: "14px 22px", borderTop: `1px solid ${C.border}`, background: C.surface }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// Horizontal pipeline progress. currentStep = index into PIPELINE_STEPS, or -1.
export function PipelineStrip({ currentStep = -1 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", marginTop: 10 }}>
      {PIPELINE_STEPS.map((step, i) => {
        const done = i < currentStep;
        const active = i === currentStep;
        const color = active ? C.purple : done ? C.amber : C.border;
        const textColor = active ? C.purple : done ? C.text : C.muted;
        return (
          <div key={step} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: active || done ? color : "transparent",
                  border: `1.5px solid ${color}`,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: 10,
                  color: textColor,
                  fontWeight: active ? 500 : 400,
                  whiteSpace: "nowrap",
                }}
              >
                {step}
              </span>
            </div>
            {i < PIPELINE_STEPS.length - 1 && (
              <span style={{ width: 12, height: 1, background: done ? C.amber : C.border }} />
            )}
          </div>
        );
      })}
    </div>
  );
}
